import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { addDays, getDate, isBefore, isToday, startOfToday } from 'date-fns';

import { PRIMARY_BLUE, LIGHT_GREY, WHITE } from '~/Constants/Colors.constants';
import { IEvent } from '~/Models/DTO/Event.model';
import { makeStyles } from '~/UIKit/Style';
import { getStartOfWeek, useUser } from '~/Hooks';
import {
  DAYS_IN_WEEK,
  EVENT_OFFSET_TOP,
  EVENT_Z_INDEX,
  MARGIN_BETWEEN_EVENTS,
  TIME_TABLE_OFFSET_TOP,
  TIME_WIDTH,
} from '~/Screens/Calendar/constants';
import { selectGroupedEventsByDate } from '~/store/events';
import classNames from 'classnames';
import { FONT_SIZE_SMALLISH } from '~/Constants/UI.constants';
import { groupEvents } from '../utils';
import {
  calculateEventPositionForWeek,
  CalendarDay,
  CalendarDayHeader,
  CalendarHeaderContainer,
  CalendarViewContent,
  CreatingEvent,
  EditCalendarEventConfirmationModal,
  ICalculateEventPosition,
  UpdatingEvent,
} from '~/Screens/Calendar/CalendarView/components';
import { ICalendarDayEvent, ICalendarOffset } from '~/Screens/Calendar/interfaces';
import {
  useUpdateCalendarEvent,
  useUpdateFullDayActivity,
} from '~/Screens/Calendar/CalendarView/hooks';
import { CalendarSkeletonEvent } from '../components/CalendarSkeletonEvent';

export const useStyles = makeStyles(
  () => ({
    weekContent: {
      width: `calc(100% - ${TIME_WIDTH + MARGIN_BETWEEN_EVENTS}px)`,
      display: 'flex',
      position: 'absolute',
    },
    days: {
      width: 'calc(100% / 7)',
      borderRight: `1px solid  ${LIGHT_GREY}`,
      '&:last-child': {
        borderRight: 'none',
      },
    },
    today: {
      color: PRIMARY_BLUE,
    },
    weekHeader: {
      width: '100%',
      height: '100%',
      zIndex: EVENT_Z_INDEX,
      fontSize: FONT_SIZE_SMALLISH,
      position: 'relative',
      borderRight: `1px solid  ${LIGHT_GREY}`,
      backgroundColor: WHITE,
      '&:last-child': {
        borderRight: 'none',
      },
    },
    weekday: {
      padding: '10px 0 10px 5px',
    },
    dateNumber: {
      fontWeight: 'bold',
    },
  }),
  { name: 'WeeklyView' },
);

interface IWeekdays {
  weekday: string;
  dateNumber: number;
  isToday: boolean;
  isPast: boolean;
}

interface IMonthlyView {
  selectedDay: Date;
  from: string | null;
  to: string | null;
  isLoading?: boolean;
}

export const WeeklyView = ({ selectedDay, from, to, isLoading = false }: IMonthlyView) => {
  const { classes } = useStyles();
  const { currentUser, role } = useUser();

  const [offset, setOffset] = useState<ICalendarOffset>({ width: 0, height: 0 });
  const [calendarDayClicked, setCalendarDayClicked] = useState<ICalendarDayEvent>(null);
  const [headerOffset, setHeaderOffset] = useState<ICalendarOffset>({
    width: 0,
    height: 0,
  });
  const startOfWeek = getStartOfWeek(selectedDay.toISOString(), 1).toISOString();
  const [weekdays, setWeekdays] = useState<IWeekdays[]>([]);
  const events: IEvent[][] = useSelector(
    useMemo(() => selectGroupedEventsByDate(new Date(startOfWeek), currentUser, role), [
      startOfWeek,
    ]),
  );
  const groupedEvents = useMemo(
    () => events.map((dayEvents) => groupEvents({ events: dayEvents })),
    [events],
  );

  const dayOffset = useMemo(() => ({ ...offset, width: offset.width / DAYS_IN_WEEK }), [offset]);
  const hasToday = useMemo(() => weekdays.some(({ isToday }) => isToday), [weekdays]);
  const getEventPosition = useCallback(
    ({ item, differenceFromInitialOffset }: Omit<ICalculateEventPosition, 'offset'>) =>
      calculateEventPositionForWeek({
        item,
        offset: dayOffset,
        differenceFromInitialOffset,
      }),
    [dayOffset],
  );
  const getFullDayActivityPosition = useCallback(
    ({ item, differenceFromInitialOffset }: Omit<ICalculateEventPosition, 'offset'>) =>
      calculateEventPositionForWeek({
        item,
        offset: { ...headerOffset, width: dayOffset.width },
        differenceFromInitialOffset,
      }),
    [headerOffset, dayOffset],
  );
  const {
    updateEvent,
    updatingEvent,
    onCloseEditEvent,
    onConfirmEditEvent,
    confirmationOptions,
  } = useUpdateCalendarEvent({
    to,
    from,
    offset: dayOffset,
    getEventPosition,
  });
  const {
    updateFullDayActivity,
    updatingFullDayActivity,
    onCloseEditFullDayActivity,
    onConfirmEditFullDayActivity,
    confirmationOptionsForFullDayActivity,
  } = useUpdateFullDayActivity({
    getEventPosition,
  });

  useEffect(() => {
    const days = [];
    const todayDate = startOfToday();

    for (let i = 0; i <= 6; i++) {
      const day = addDays(new Date(startOfWeek), i);
      days.push({
        isPast: isBefore(day, todayDate),
        isToday: isToday(day),
        weekday: day.toLocaleString('default', { weekday: 'short' }),
        dateNumber: getDate(day),
      });
    }
    setWeekdays(days);
  }, [startOfWeek]);

  return (
    <>
      <CalendarHeaderContainer
        offset={offset}
        headerOffset={headerOffset}
        setHeaderOffset={setHeaderOffset}
        getEventPosition={getFullDayActivityPosition}
      >
        {dayOffset.width > 0 &&
          weekdays?.map(({ weekday, isToday, dateNumber, isPast }, idx) => (
            <CalendarDayHeader
              key={weekday}
              width={dayOffset.width}
              isPast={isPast}
              events={groupedEvents?.[idx]?.fullDayActivity}
              className={classNames(classes.weekHeader, { [classes.today]: isToday })}
              daysOffset={idx}
              updateFullDayActivity={updateFullDayActivity}
            >
              <div className={classes.weekday}>
                <div>{weekday}</div>
                <div className={classes.dateNumber}>{dateNumber}</div>
              </div>
            </CalendarDayHeader>
          ))}
        <UpdatingEvent eventData={updatingFullDayActivity} top={headerOffset.top} />
        {confirmationOptionsForFullDayActivity && (
          <EditCalendarEventConfirmationModal
            eventName={updatingFullDayActivity?.event.name}
            onCloseEditEvent={onCloseEditFullDayActivity}
            onConfirmEditEvent={onConfirmEditFullDayActivity}
            confirmationOptions={confirmationOptionsForFullDayActivity}
          />
        )}
      </CalendarHeaderContainer>
      <CalendarViewContent
        offset={offset}
        hasToday={hasToday}
        setOffset={setOffset}
        getEventPosition={getEventPosition}
      >
        <div className={classes.weekContent}>
          {isLoading && <CalendarSkeletonEvent />}
          {!isLoading &&
            dayOffset.width > 0 &&
            weekdays?.map(({ weekday }, idx) => (
              <CalendarDay
                key={weekday}
                offset={dayOffset}
                events={groupedEvents?.[idx]?.usualEvents}
                position="relative"
                className={classes.days}
                daysOffset={idx}
                updateEvent={updateEvent}
                getEventPosition={getEventPosition}
                onCalendarDayClicked={setCalendarDayClicked}
              />
            ))}
        </div>
        <CreatingEvent
          top={TIME_TABLE_OFFSET_TOP + EVENT_OFFSET_TOP}
          offset={dayOffset}
          startOfPeriod={startOfWeek}
          contentPlacement="right"
          calendarDayEvent={calendarDayClicked}
        />
        <UpdatingEvent top={TIME_TABLE_OFFSET_TOP + EVENT_OFFSET_TOP} eventData={updatingEvent} />
        {confirmationOptions && (
          <EditCalendarEventConfirmationModal
            eventName={updatingEvent?.event.name}
            onCloseEditEvent={onCloseEditEvent}
            onConfirmEditEvent={onConfirmEditEvent}
            confirmationOptions={confirmationOptions}
          />
        )}
      </CalendarViewContent>
    </>
  );
};
